import get from "lodash/get";
import map from "lodash/map";
import dayjs from "dayjs";
import assign from "lodash/assign";
import isNull from "lodash/isNull";
import round from "lodash/round";
import { getTransformedMinutes } from "../../../utilities/helpers";
import { performanceTemplateTypeConfig, performanceTemplateDataSource } from "./performanceTemplateTypeConfig"

const checkNullable = (value, defaultVal = 0) => {
  return isNull(value) ? defaultVal : value;
};

const getColor = (category) => {
  switch (category) {
    case "Basic":
      return "#0277bd";
    case "OEE":
      return "#4caf50";
    case "Time":
      return "#ffab40";
    case "Cycle time":
      return "#6a1b9a";
    case "Count":
      return "#607d8b";
    case "Down Time":
      return "#ef5350";
    case "Prod.Time":
      return "#9e9e9e";
    default:
      return "grey";
  }
};

const fontStyle = {
  fontWeight: "800",
};

export const getPerformanceReportColumn = (report) => {
  return [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      fixed: "left",
      width: 10,
      onCell: (record, rowIndex) => ({
        rowSpan: record.rowSpan || 0,
        style: {
          ...fontStyle,
          backgroundColor: getColor(get(record, "category")),
        },
      }),
      render: (text) => (
        <div className="h-[100px] flex items-center justify-center ">
          <span className="rotate-90 writing-mode-vertical-rl text-center text-white">
            {text}
          </span>
        </div>
      ),
    },
    {
      title:"Parameter",
      dataIndex: "basic",
      key: "basic",
      fixed: "left",
      align: 'center',
      width: 150,
    },
    {
      title: "Shift",
      align: 'center',
      children: map(get(report, "shifts"), (shift, shiftIndex) => ({
        title: get(shift, "name"),
        align: 'center',
        dataIndex: (!get(shift, "components") || get(shift, "components.length") === 0) && `${get(shift, "id")}`,
        children: map(get(shift, "components"), (component, ind) => ({
          title: (
            <div className="text-center">
              <p>PC:- {get(component, "name")}</p>
              <br />
              <p className="text-xs">
                {" "}
                Time:- [
                 {dayjs(get(component, "start_time"), "HH:mm:ss").format(
                  "HH:mm A"
                )}{" "}
                -{" "}
                {dayjs(get(component, "end_time"), "HH:mm:ss").format(
                  "HH:mm A"
                )}
                ]
              </p>
            </div>
          ),
          children: map(get(component, "parts"), (part, index) => ({
            title: `Part:- ${get(part, "name")}`,
            align: 'center',
            dataIndex: `${get(shift, "id")}-${(shift, "name")}-${shiftIndex + 1
              }-${get(component, "name")}-${ind}-part-${get(
                part,
                "name"
              )}-summary-${index + 1}`,
            onCell: (record) => {
              if (
                get(record, "dataKey") === "operator" ||
                get(record, "dataKey") === "total_off_hours" ||
                get(record, "dataKey") === "part_meta.total_working_hours" ||
                get(record, "dataKey") === "meta.total_idle_hours" ||
                get(record, "dataKey") ===
                "part_meta.total_power_consumption" ||
                get(record, "dataKey") === "part_meta.avg_cycle_time" ||
                get(record, "dataKey") === "part_meta.mhr_loss" ||
                get(record, "dataKey") === "wages_per_hour"
              ) {
                if (index === 0) {
                  return { colSpan: get(component, "parts.length", []) };
                }
                return { colSpan: 0 };
              }
              return {};
            },
          })),
        })),
      })),
    },
    {
      title: "Day Summary",
      dataIndex: "daySummary",
      key: "daySummary",
      fixed: "right",
      align: 'center',
    },
  ];
};

export const generateDataSource = (report, templateType) => {
  const config = {
    operator: {
      dataKey: "operator",
      shiftDataKey: "operator",
      daySummary: "--",
      suffix: "",
    },
    availabilty_ratio: {
      dataKey: "part_meta.availablity_ratio",
      shiftDataKey: "shift_meta.availablity_ratio",
      daySummary: `${checkNullable(
        get(report, "template_meta.availablity_ratio")
          ? get(report, "template_meta.availablity_ratio")
          : 0
      )} %`,
      suffix: "%",
      inHour: false,
    },
    machine_efficiency: {
      dataKey: "part_meta.machine_efficiency",
      shiftDataKey: "shift_meta.machine_efficiency",
      daySummary: `${checkNullable(
        get(report, "template_meta.machine_efficiency")
          ? get(report, "template_meta.machine_efficiency")
          : 0
      )} %`,
      suffix: "%",
      inHour: false,
    },
    quality_ratio: {
      dataKey: "part_meta.quality_ratio",
      shiftDataKey: "shift_meta.quality_ratio",
      daySummary: `${checkNullable(
        get(report, "template_meta.quality_ratio")
          ? get(report, "template_meta.quality_ratio")
          : 0
      )} %`,
      suffix: "%",
      inHour: false,
    },
    oee: {
      dataKey: "part_meta.oee",
      shiftDataKey: "shift_meta.oee",
      daySummary: `${checkNullable(
        get(report, "template_meta.oee") ? get(report, "template_meta.oee") : 0
      )} %`,
      suffix: "%",
      inHour: false,
    },
    on_time: {
      dataKey: "part_meta.total_working_hours",
      shiftDataKey: "shift_meta.total_working_hours",
      daySummary: `${get(report, "template_meta.total_working_hours")
          ? getTransformedMinutes(
            get(report, "template_meta.total_working_hours")
          )
          : 0
        }`,
      suffix: "min",
      inHour: true,
    },
    off_time: {
      dataKey: "total_off_hours",
      shiftDataKey: "shift_meta.total_off_hours",
      daySummary: `${get(report, "template_meta.total_off_hours")
          ? getTransformedMinutes(get(report, "template_meta.total_off_hours"))
          : 0
        }`,
      suffix: "min",
      inHour: true,
    },
    idle_time: {
      dataKey: "meta.total_idle_hours",
      shiftDataKey: "shift_meta.total_idle_hours",
      daySummary: `${get(report, "template_meta.total_idle_hours")
          ? getTransformedMinutes(get(report, "template_meta.total_idle_hours"))
          : 0
        }`,
      suffix: "min",
      inHour: true,
    },
    wages: {
      dataKey: "wages_per_hour",
      shiftDataKey: "shift_meta.wages",
      daySummary: `${get(report, "template_meta.total_wages")
          ? get(report, "template_meta.total_wages")
          : 0
        }`,
      suffix: "",
      inHour: false,
    },
    mhr_loss: {
      dataKey: "part_meta.mhr_loss",
      shiftDataKey: "shift_meta.mhr_losses",
      daySummary: `${get(report, "template_meta.total_mhr_loss")
          ? round(get(report, "template_meta.total_mhr_loss"), 2)
          : 0
        }`,
      suffix: "",
      inHour: false,
    },
    ...performanceTemplateTypeConfig(report, templateType),
    available_time: {
      dataKey: "part_meta.available_time",
      shiftDataKey: "shift_meta.available_time",
      daySummary: `${get(report, "template_meta.available_time")
          ? getTransformedMinutes(get(report, "template_meta.available_time"))
          : 0
        }`,
      inHour: true,
      suffix: "",
    },
    planned_down_time: {
      dataKey: "part_meta.planned_down_time",
      shiftDataKey: "shift_meta.planned_down_time",
      daySummary: `${get(report, "template_meta.planned_down_time")
          ? getTransformedMinutes(
            get(report, "template_meta.planned_down_time")
          )
          : 0
        }`,
      inHour: true,
      suffix: "",
    },
    power_consumption: {
      dataKey: "part_meta.total_power_consumption",
      shiftDataKey: "shift_meta.total_power_consumption",
      daySummary: `${get(report, "template_meta.total_power_consumption")
          ? get(report, "template_meta.total_power_consumption")
          : 0
        } Kwh`,
      suffix: " Kwh",
      inHour: false,
    },
  };

  map(get(report, "shifts"), (shiftReport, shiftIndex) => {
    if (!get(shiftReport, "components") || get(shiftReport, "components.length") === 0) {
      map(config, (c, key) => {
        if (get(c, "shiftDataKey") === "operator") {
          config[key][get(shiftReport, "id")] = `${get(shiftReport, `${get(c, "shiftDataKey")}`, "--") || "--"
            } ${get(c, "suffix")}`;
        } else {
          if (get(c, "inHour")) {
            config[key][get(shiftReport, "id")] = `${getTransformedMinutes(get(shiftReport, `${get(c, "shiftDataKey")}`, 0))
              || 0
              }`;
          } else {
            config[key][get(shiftReport, "id")] = `${get(shiftReport, `${get(c, "shiftDataKey")}`, 0) || 0
              } ${get(c, "suffix")}`;
          }
        }
      });
    }
    map(get(shiftReport, "components"), (component, ind) => {
      map(get(component, "parts"), (part, index) => {
        const partKey = `${get(shiftReport, "id")}-${(shiftReport, "name")}-${shiftIndex + 1
          }-${get(component, "name")}-${ind}-part-${get(part, "name")}-summary-${index + 1
          }`;
        map(config, (c, key) => {
          if (get(c, "inHour")) {
            config[key][partKey] = `${get(part, get(c, "dataKey"))
                ? getTransformedMinutes(get(part, get(c, "dataKey")))
                : getTransformedMinutes(get(shiftReport, `shift_meta.${get(c, "dataKey")}`, 0))
              }`;
          } else {
            config[key][partKey] = `${get(part, get(c, "dataKey"))
                ? get(part, get(c, "dataKey"))
                : get(shiftReport, `shift_meta.${get(c, "dataKey")}`, 0)
              } ${get(c, "suffix")}`;
          }
        });
      });
    });
  });

  config["operator"][`daySummary`] = "--";

  assign(config, {
    start_time: {
      daySummary: "--",
    },
    end_time: {
      daySummary: "--",
    },
    total_down_time: {
      daySummary: `--`,
    },
  });

  let totalDownTime = 0;

  map(get(report, "shifts"), (shift, shiftIndex) => {
    if (!get(shift, "components") || get(shift, "components.length") === 0) {
      config["power_consumption"][get(shift, "id")] = `${get(shift, "shift_meta.total_power_consumption") || 0
        } Kwh`;
      switch (templateType) {
        case 1:
          config["actual_cycle_time"][get(shift, "id")] = get(
            shift,
            "shift_meta.avg_cycle_time"
          )
            ? get(shift, "shift_meta.avg_cycle_time")
            : 0;
      }
      config["mhr_loss"][get(shift, "id")] = get(shift, "shift_meta.mhr_losses")
        ? round(get(shift, "shift_meta.mhr_losses"), 2)
        : 0;
      config["wages"][get(shift, "id")] = get(shift, "shift_meta.wages")
        ? round(get(shift, "shift_meta.wages"), 2)
        : 0;
      const down_dt =
        get(shift, `shift_meta.planned_down_time`, 0) +
        get(shift, `shift_meta.total_off_hours`, 0);

      config["total_down_time"][get(shift, "id")] = `${getTransformedMinutes(down_dt)}`;
      totalDownTime = totalDownTime + down_dt;
    }
    map(get(shift, "components", [{ name: "" }]), (component, ind) => {
      map(get(component, "parts"), (part, index) => {
        const partKey = `${get(shift, "id")}-${(shift, "name")}-${shiftIndex + 1
          }-${get(component, "name")}-${ind}-part-${get(part, "name")}-summary-${index + 1
          }`;
        config["operator"][partKey] = get(shift, "operator");
        config["on_time"][partKey] = getTransformedMinutes(
          get(component, "total_working_hours") || 0
        );
        config["off_time"][partKey] = getTransformedMinutes(
          get(component, "total_off_hours") || 0
        );
        config["idle_time"][partKey] = getTransformedMinutes(
          get(component, "total_idle_hours") || 0
        );
        config["power_consumption"][partKey] = `${get(
          component,
          "total_power_consumption"
        )} Kwh`;
        switch (templateType) {
          case 1:
            config["actual_cycle_time"][partKey] = get(
              component,
              "component_meta.avg_cycle_time"
            )
              ? get(component, "component_meta.avg_cycle_time")
              : 0;
        }
        config["mhr_loss"][partKey] = get(
          component,
          "component_meta.mhr_losses"
        )
          ? round(get(component, "component_meta.mhr_losses"), 2)
          : 0;
        config["wages"][partKey] = get(component, "component_meta.wages")
          ? round(get(component, "component_meta.wages"), 2)
          : 0;
        const down_dt =
          get(part, `part_meta.planned_down_time`, 0) +
          get(part, `part_meta.total_off_hours`, 0);

        config["total_down_time"][partKey] = `${getTransformedMinutes(down_dt)}`;
        totalDownTime = totalDownTime + down_dt;
      });
    });
  });
  config["total_down_time"]["daySummary"] = `${getTransformedMinutes(totalDownTime)}`;
  return [
    {
      key: "1",
      category: "Basic",
      basic: "Operator",
      ...get(config, "operator"),
      rowSpan: 1,
    },
    {
      key: "2",
      category: "OEE",
      basic: "Availability",
      ...get(config, "availabilty_ratio"),
      rowSpan: 4,
    },
    {
      key: "3",
      category: "OEE",
      basic: "Performance",
      ...get(config, "machine_efficiency"),
      rowSpan: 0,
    },
    {
      key: "4",
      category: "OEE",
      basic: "Quality",
      ...get(config, "quality_ratio"),
      rowSpan: 0,
    },
    {
      key: "5",
      category: "OEE",
      basic: "OEE",
      ...get(config, "oee"),
      rowSpan: 0,
    },
    {
      key: "6",
      category: "Time",
      basic: "On Time",
      ...get(config, "on_time"),
      rowSpan: 5,
    },
    {
      key: "7",
      category: "Time",
      basic: "Off Time",
      ...get(config, "off_time"),
      rowSpan: 0,
    },
    {
      key: "8",
      category: "Time",
      basic: "Idle Time",
      ...get(config, "idle_time"),
      rowSpan: 0,
    },
    {
      key: "9",
      category: "Time",
      basic: "Wages",
      ...get(config, "wages"),
      rowSpan: 0,
    },
    {
      key: "10",
      category: "Time",
      basic: "MHR Loss",
      ...get(config, "mhr_loss"),
      rowSpan: 0,
    },
    ...performanceTemplateDataSource(templateType, config),
    {
      key: "16",
      category: "Down Time",
      basic: "Available Time",
      ...get(config, "available_time"),
      rowSpan: 3,
    },
    {
      key: "17",
      category: "Down Time",
      basic: "Planned Down Time",
      ...get(config, "planned_down_time"),
      rowSpan: 0,
    },
    // {
    //   key: "18",
    //   category: "Down Time",
    //   basic: "Unplanned Down Time",
    //   ...get(config, "unplanned_down_time"),
    //   rowSpan: 0,
    // },
    {
      key: "19",
      category: "Down Time",
      basic: "Total dt",
      ...get(config, "total_down_time"),
      rowSpan: 0,
    },
    {
      key: "21",
      category: "Power Consumption",
      basic: "Power Consumption",
      ...get(config, "power_consumption"),
      rowSpan: 1,
    },
  ];
};
